/**
 * Traditions index — the new Lexicon root.
 *
 * Shows the canonical traditions catalogue grouped by region/era, mirroring
 * the "Traditions that feel like home" chooser in Settings but expanded
 * with Western Astrology, Vedic Astrology, and Human Design.
 *
 * Each tile links to /traditions/<name-with-dashes>, which renders the
 * full breakdown (beliefs, practices, figures, correlations).
 */
import React from "react";
import { Link } from "react-router-dom";
import { TRADITIONS } from "../lib/traditions";
import TraditionsTabs from "../components/TraditionsTabs";
import TraditionsSearch from "../components/TraditionsSearch";
import PullToRefresh from "../components/PullToRefresh";

// Convert tradition name → URL-safe path segment.
// "Vedic Astrology" → "Vedic-Astrology". Reversed by TraditionDetail.
export const traditionPath = (t) => `/traditions/${encodeURIComponent(t.name.replace(/\s+/g, "-"))}`;

// Region/era groups — order matters (reads roughly chronologically + by region).
// Each group gathers slugs from /lib/traditions.js. Anything new added there
// without a group ends up under "Other paths" at the bottom.
const GROUPS = [
  {
    key: "eastern",
    title: "Eastern paths",
    blurb: "The dharmic, contemplative streams of South + East Asia.",
    slugs: ["hinduism", "vedanta", "buddhism", "tibetan-buddhism", "taoism", "tantra", "vedic-astrology"],
  },
  {
    key: "abrahamic-mystical",
    title: "Abrahamic + mystical",
    blurb: "Faiths of the book and their inward, mystical hearts.",
    slugs: ["judaism", "christianity", "islam", "sufism", "kabbalah", "book-of-enoch", "gnosticism"],
  },
  {
    key: "classical-esoteric",
    title: "Classical + esoteric",
    blurb: "Greco-Roman philosophy and the Western esoteric current.",
    slugs: ["stoicism", "platonism", "hermeticism", "alchemy"],
  },
  {
    key: "indigenous",
    title: "Indigenous + ancestral",
    blurb: "Living cosmologies rooted in land, story, and ceremony.",
    slugs: ["indigenous-australian", "mesoamerican", "siberian-shamanism"],
  },
  {
    key: "modern-syntheses",
    title: "Modern syntheses",
    blurb: "20th- and 21st-century frameworks weaving older threads anew.",
    slugs: ["jungian-psychology", "modern-physics", "quantum-physics", "western-astrology", "human-design", "law-of-one"],
  },
  {
    key: "open-questioning",
    title: "Open + questioning",
    blurb: "Stances that travel light — open to evidence, open to mystery.",
    slugs: ["secular-skeptic", "spiritual-but-not-religious"],
  },
];

function pickGroups() {
  const seen = new Set();
  const grouped = GROUPS.map((g) => {
    const items = g.slugs
      .map((s) => TRADITIONS.find((t) => t.slug === s))
      .filter(Boolean);
    items.forEach((t) => seen.add(t.slug));
    return { ...g, items };
  });
  const leftovers = TRADITIONS.filter((t) => !seen.has(t.slug));
  if (leftovers.length > 0) {
    grouped.push({
      key: "other",
      title: "Other paths",
      blurb: "",
      items: leftovers,
    });
  }
  return grouped;
}

export default function Traditions() {
  const groups = pickGroups();

  const onPullRefresh = async () => {
    // Static catalogue — nothing to refetch, but honour the gesture.
    return Promise.resolve();
  };

  return (
    <PullToRefresh onRefresh={onPullRefresh}>
      <div data-testid="traditions-page" className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
        <TraditionsTabs />

        <div className="label mb-6 text-[hsl(var(--accent))]">The Lexicon</div>
        <h1 className="serif text-5xl sm:text-6xl text-foreground max-w-3xl">
          Traditions, side by side.
        </h1>
        <p className="mt-6 text-muted-foreground max-w-2xl leading-relaxed">
          Every path here points, in its own tongue, toward the same horizon.
          Tap any tradition for its beliefs, practices, key figures, and the
          terms it shares with the others.
        </p>

        {/* SEARCH — autocomplete jump to any tradition's detail page */}
        <div className="mt-10">
          <TraditionsSearch />
        </div>

        <div className="mt-16 space-y-16">
          {groups.map((g) => (
            <section key={g.key} data-testid={`tradition-group-${g.key}`}>
              <div className="hairline-b pb-3 mb-7 flex items-baseline justify-between gap-4 flex-wrap">
                <h2 className="serif text-2xl sm:text-3xl text-foreground">{g.title}</h2>
                {g.blurb && (
                  <p className="text-muted-foreground/85 text-sm italic max-w-md">
                    {g.blurb}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {g.items.map((t) => (
                  <Link
                    key={t.slug}
                    to={traditionPath(t)}
                    data-testid={`tradition-card-${t.slug}`}
                    className="group p-6 hairline hover:border-[hsl(var(--primary))] transition-colors bg-background/40 hover:bg-background flex flex-col"
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <h3 className="serif text-xl sm:text-2xl text-foreground group-hover:text-[hsl(var(--primary))] transition-colors">
                        {t.name}
                      </h3>
                    </div>
                    <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 mb-3">
                      {t.era}
                    </div>
                    <p className="text-[14px] text-foreground/80 leading-relaxed flex-1">
                      {t.summary}
                    </p>
                    <div className="mt-4 mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))] opacity-0 group-hover:opacity-100 transition-opacity">
                      Read →
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </PullToRefresh>
  );
}
